// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import SwitchUserModal from '../../components/ShiftSettingsModal/SwitchUserModal';
import AddUserModal from '../../components/ShiftSettingsModal/AddUserModal';
import DeleteUserModal from '../../components/ShiftSettingsModal/DeleteUserModal';
import { USER_MODAL_TYPES } from '../../components/ShiftSettingsModal/constants';
import StampTimeoutModal from '../../components/StampTimeoutModal';
import ShiftTimeoutModal from '../../components/ShiftTimeoutModal';

import { AuthContext } from '../../context/AuthContext';
import { ThemeContext } from '../../context/ThemeContext';
import { auth, firestore } from '../../context/FirebaseConfig';
import { signOut } from 'firebase/auth';

import { updateDoc, doc } from 'firebase/firestore';
import { addMinutes } from 'date-fns';

// Component imports & hooks
import TopNavigation from './components/TopNavigation';
import SideMenu from './components/SideMenu';
import ButtonGrid from './components/ButtonGrid';
import LogoutConfirmationModal from '../../components/LogoutConfirmationModal';
import { useVelocityData } from './hooks/useVelocityData';
import { useFirestoreSubscriptions } from './hooks/useFirestoreSubscriptions';
import { useDocumentManagement } from './hooks/useDocumentManagement';
import { useTranslation } from '../../i18n/i18n';

import RecipeDisplay from '../RecipeDisplay/RecipeDisplay';
import LoadingScreen from '../../components/LoadingScreen';
import { RecipeDisplayProvider } from '../../context/RecipeDisplayContext';

import {
  subscribeToStampAppRecipeControlSettings,
  subscribeToStockFields,
  subscribeToSuppliers,
} from '../../shared_components/utils/FirebaseUtils';
import { getCurrentRunningProduction } from '../../utils/recipes/recipeControl';
import { getElapsedSeconds } from '../../utils/timeUtils';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';

/**
 * The identity of a stamp as logged: ids only, and an unset value as an explicit null so a stamp
 * without a material is distinguishable from one whose material was never recorded.
 *
 * @param {Object} stamp - A button selection or an active document's data.
 * @returns {Object} Event params describing which stamp was acted on.
 */
function stampEventParams(stamp) {
  return {
    supplier_id: stamp?.supplier_id || null,
    field_id: stamp?.field || null,
    material_id: stamp?.material || null,
  };
}

export default function FeedTimestamping() {
  const { currentUser, setEventLogOperator, logEventCustom } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const { uiMode, updateUiMode } = React.useContext(ThemeContext);
  const [buttonSelection, setButtonSelection] = React.useState(
    Array(4).fill({ supplier_id: '', field: '', material: '' }),
  );
  const [shiftInfo, setShiftInfo] = React.useState({
    user: '',
    feed_shift: 'A',
    work_shift: 'early',
  });
  const [deviceName, setDeviceName] = React.useState('');
  const [isLoadingInitialData, setIsLoadingInitialData] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [activeDocument, setActiveDocument] = React.useState(false);
  const [confirmLogout, setConfirmLogout] = React.useState(false);
  const [settings, setSettings] = React.useState({});
  const [suppliers, setSuppliers] = React.useState({});
  const [stockFields, setStockFields] = React.useState({});
  const [activeUserModal, setActiveUserModal] = React.useState(null);
  const [showStampTimeoutModal, setShowStampTimeoutModal] = React.useState(false);
  const [showShiftTimeoutModal, setShowShiftTimeoutModal] = React.useState(false);
  const [nRows, setNRows] = React.useState(2);
  const [nColumns, setNColumns] = React.useState(2);
  const [draftRows, setDraftRows] = React.useState(2);
  const [draftColumns, setDraftColumns] = React.useState(2);
  const [draftButtonSelection, setDraftButtonSelection] = React.useState(
    Array(4).fill({ supplier_id: '', field: '', material: '' }),
  );
  // Used to skip the nRows/nColumns useEffect double-write when saving from edit mode
  const isSavingLayout = React.useRef(false);
  const [linkedDevices, setLinkedDevices] = React.useState([]);
  const [showSideMenu, setShowSideMenu] = React.useState(false);
  const [conveyorCode, setConveyorCode] = React.useState('BAND');
  const [productionRuns, setProductionRuns] = React.useState([]);
  const [productionEvents, setProductionEvents] = React.useState([]);
  const [ingredients, setIngredients] = React.useState({});
  const [appSettings] = React.useState({});

  const [isRecipeControlEnabled, setIsRecipeControlEnabled] = React.useState(false);

  // Custom hooks for better organization
  const { velocities } = useVelocityData(currentUser);

  const {
    subscribeToButtonSelection,
    subscribeToBaleFeedSettings,
    subscribeToActiveDocuments,
    subscribeToProductions,
    subscribeToProductionEvents,
    subscribeToIngredients,
  } = useFirestoreSubscriptions(
    currentUser,
    setButtonSelection,
    setShiftInfo,
    setNRows,
    setNColumns,
    setLinkedDevices,
    setDeviceName,
    setSettings,
    setActiveDocument,
    setConveyorCode,
    setProductionRuns,
    setIngredients,
    updateUiMode,
  );

  const {
    closeActiveDocument,
    extendStampTimeout,
    openActiveDocument,
    updateButtonSelection,
    isButtonActive,
    closeActiveDocumentsByIds,
  } = useDocumentManagement(currentUser, activeDocument, linkedDevices, settings, shiftInfo);

  // Enrich production runs with ingredient data once; pass to RecipeDisplay and ButtonGrid.
  // Spread order: run prop first (batch_size, ratio, fields), then ingredient (name, material_attributes), then prop again so run data wins.
  const enrichedProductionRuns = React.useMemo(() => {
    if (!productionRuns?.length) return [];
    return productionRuns.map((run) => {
      if (!run.production_properties) return run;
      return {
        ...run,
        production_properties: run.production_properties.map((prop) => ({
          ...prop,
          ...(ingredients && typeof ingredients === 'object' && !Array.isArray(ingredients)
            ? ingredients[prop.ingredient_id] || {}
            : {}),
          ...prop,
        })),
      };
    });
  }, [productionRuns, ingredients]);

  // Subscribe to firebase data for live updates
  React.useEffect(() => {
    const unsubscribes = [
      subscribeToButtonSelection(),
      subscribeToBaleFeedSettings(),
      subscribeToActiveDocuments(closeActiveDocumentsByIds),
      subscribeToSuppliers(firestore, currentUser.company.company, setSuppliers),
      subscribeToStockFields(firestore, currentUser.company.company, setStockFields),
      subscribeToStampAppRecipeControlSettings(firestore, currentUser.company.company, setIsRecipeControlEnabled),
    ];

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
  }, []);

  React.useEffect(() => {
    if (deviceName) {
      setIsLoadingInitialData(false);
    }
  }, [deviceName]);

  // The shift subscription lives here, so the event logger is told who is on the device.
  React.useEffect(() => {
    setEventLogOperator(shiftInfo?.user ?? null);
  }, [shiftInfo?.user, setEventLogOperator]);

  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(interval);
  }, []);

  const currentRunningProduction = React.useMemo(
    () => getCurrentRunningProduction(productionRuns, now),
    [productionRuns, now],
  );
  const currentRunningProductionId = currentRunningProduction?.id || null;

  React.useEffect(() => {
    if (!isRecipeControlEnabled) return undefined;

    const unsubscribeProductions = subscribeToProductions();
    const unsubscribeIngredients = subscribeToIngredients();

    return () => {
      if (unsubscribeProductions) unsubscribeProductions();
      if (unsubscribeIngredients) unsubscribeIngredients();
    };
  }, [isRecipeControlEnabled]);

  React.useEffect(() => {
    if (!isRecipeControlEnabled) return undefined;
    return subscribeToProductionEvents(currentRunningProduction, setProductionEvents);
  }, [isRecipeControlEnabled, currentRunningProductionId, currentRunningProduction]);

  // Set timers for active stamp timeouts
  React.useEffect(() => {
    setShowStampTimeoutModal(false);

    // No active document
    if (!activeDocument || !activeDocument.data?.expiration_timestamp) {
      return;
    }

    const timeToWarning = activeDocument.data.expiration_timestamp.toDate() - new Date();
    const timer = setTimeout(() => {
      setShowStampTimeoutModal(true);
      logEventCustom(EVENT_NAMES.STAMP_TIMEOUT_SHOWN, {
        duration_seconds: getElapsedSeconds(activeDocument.data.start_timestamp),
      });
    }, timeToWarning);

    return () => clearTimeout(timer);
  }, [activeDocument, logEventCustom]);

  // Ensure active document has a matching button
  React.useEffect(() => {
    if (!activeDocument) {
      return;
    }
    // Check if document has material
    const docMaterial = activeDocument.data.material;

    // check the active button selection has the same [supplier, field, material] as the active document
    // Also check defensively if the material doesn't exist (old format).
    if (
      buttonSelection.some(
        (button) =>
          button.supplier_id === activeDocument.data.supplier_id &&
          button.field === activeDocument.data.field &&
          // If docMaterial is null/undefined, match any button with same supplier/field
          // Otherwise, require exact material match
          (docMaterial === null || docMaterial === undefined || button.material === docMaterial),
      )
    ) {
      // The active document exists in the buttons
      return;
    }

    // Set the last button to the supplier and field of the active document
    // HOWEVER: This is not writing it to the db, just displaying it. If you keep pressing buttons in this view, it will then update it.
    const newButtonSelection = [...buttonSelection];
    newButtonSelection[newButtonSelection.length - 1] = {
      supplier_id: activeDocument?.data?.supplier_id,
      field: activeDocument?.data?.field,
      material: activeDocument?.data?.material || null, // Denfensively check for material's existance
    };
    setButtonSelection(newButtonSelection);
  }, [activeDocument]);

  // Set timer for shift ending. Add 15 minutes of margin before triggering warning
  React.useEffect(() => {
    if (!shiftInfo?.shift_expiration_timestamp) {
      return;
    }

    const timeToWarning = addMinutes(shiftInfo.shift_expiration_timestamp.toDate(), 15) - new Date();

    const timer = setTimeout(() => {
      setShowShiftTimeoutModal(true);
      logEventCustom(EVENT_NAMES.SHIFT_TIMEOUT_SHOWN, {});
    }, timeToWarning);

    return () => clearTimeout(timer);
  }, [shiftInfo, logEventCustom]);

  function selectionEdited(i, key, value) {
    /* Update the button selection at index i with the new value */
    if (isButtonActive(buttonSelection[i])) {
      // If the active button is edited, close the active document
      closeActiveDocument();
    }

    const newButtonSelection = [...buttonSelection];
    newButtonSelection[i] = { ...buttonSelection[i], [key]: value };
    updateButtonSelection(newButtonSelection, nRows, nColumns, linkedDevices);
  }

  function toggleSelection(selection) {
    /* Toggle the button with the given selection. Then trigger the update of the stamp documents */
    if (isButtonActive(selection)) {
      // Logged before the local state is cleared, while the stamp being stopped is still readable.
      // The stamp writes are deliberately not awaited here, so these events record what the operator
      // did rather than a confirmed write.
      logEventCustom(EVENT_NAMES.STAMP_STOP, {
        ...stampEventParams(activeDocument?.data),
        duration_seconds: getElapsedSeconds(activeDocument?.data?.start_timestamp),
        reason: 'manual',
      });

      // The clicked button is currently active -> button is getting deactivated and no other button is getting activated
      setActiveDocument(false); // Set local state for faster responsiveness in the UI
      closeActiveDocument();
    } else {
      // Set local state for faster responsiveness in the UI
      setActiveDocument({
        id: '',
        data: {
          supplier_id: selection.supplier_id,
          field: selection.field,
          material: selection.material,
          start_timestamp: new Date(),
        },
      });

      // Deactivate active document (if it exists) and activate newly pressed button
      openActiveDocument(selection.supplier_id, selection.field, selection.material, currentUser.device_id, true);

      // Also open the active document on linked devices
      if (linkedDevices?.length > 0) {
        linkedDevices.forEach((deviceId) => {
          openActiveDocument(selection.supplier_id, selection.field, selection.material, deviceId, true);
        });
      }

      // One event per operator action, not per device written to; the fan-out is a param.
      logEventCustom(EVENT_NAMES.STAMP_START, {
        ...stampEventParams(selection),
        linked_device_count: linkedDevices?.length ?? 0,
      });
    }
  }

  function toggleEdit() {
    /* Turn on/off edit mode -> Change the button layout and supplier/fields settings */
    // Only the way in is logged; leaving is either a save (logged by saveLayout) or a discard.
    if (!editMode) {
      logEventCustom(EVENT_NAMES.LAYOUT_EDIT_START, { n_rows: nRows, n_columns: nColumns });
    }

    setEditMode((prevMode) => !prevMode);
  }

  // Sync all drafts from persisted values each time edit mode is opened
  React.useEffect(() => {
    if (editMode) {
      setDraftRows(nRows);
      setDraftColumns(nColumns);
      setDraftButtonSelection(buttonSelection);
    }
  }, [editMode]);

  // During edit mode show the draft grid; outside edit mode show the persisted grid.
  const displayNRows = editMode ? draftRows : nRows;
  const displayNColumns = editMode ? draftColumns : nColumns;

  // Source from draft in edit mode so +/- and dropdown changes are reflected
  // immediately without touching Firestore. Pad/trim to match the current grid size.
  const displayButtonSelection = React.useMemo(() => {
    const source = editMode ? draftButtonSelection : buttonSelection;
    const target = displayNRows * displayNColumns;
    if (source.length === target) return source;
    if (source.length < target) {
      return [...source, ...Array(target - source.length).fill({ supplier_id: '', field: '', material: '' })];
    }
    return source.slice(0, target);
  }, [editMode, draftButtonSelection, buttonSelection, displayNRows, displayNColumns]);

  // Local-only selection edit — updates draftButtonSelection without touching Firestore.
  // Passed to ButtonGrid in edit mode so all changes are staged until Save.
  function draftSelectionEdited(i, key, value) {
    if (isButtonActive(buttonSelection[i])) {
      closeActiveDocument();
    }
    setDraftButtonSelection((prev) => {
      const updated = [...prev];
      // Grow the array if the user is editing a slot beyond the current draft length
      while (updated.length <= i) {
        updated.push({ supplier_id: '', field: '', material: '' });
      }
      updated[i] = { ...updated[i], [key]: value };
      return updated;
    });
  }

  async function switchDevice(deviceId) {
    /** Switch the device */
    // Logged before the page reload below, which would otherwise cut the write short. The mutation
    // is queued in Firestore's local cache first, so it survives the reload and flushes after it.
    if (deviceId === 'all') {
      // No single conveyor is being selected, so there is no id to record.
      logEventCustom(EVENT_NAMES.DEVICE_SWITCH, { device_id: null, mode: 'all_linked' });

      // Instead of switching, link all other devices to the current one
      const linkedDevices = Object.keys(currentUser.devices).filter((key) => key !== currentUser.device_id);
      updateButtonSelection(buttonSelection, nRows, nColumns, linkedDevices);
      return;
    } else {
      logEventCustom(EVENT_NAMES.DEVICE_SWITCH, { device_id: deviceId, mode: 'single' });

      const linkedDevices = [];
      updateButtonSelection(buttonSelection, nRows, nColumns, linkedDevices);
    }

    // Check if the device is already selected
    if (deviceId === currentUser.device_id) {
      return;
    }

    // Update the device id in the user document
    await updateDoc(doc(firestore, 'users', currentUser.uid), { selected_device_id: deviceId });

    // Refresh the page to apply changes
    window.location.reload(false);
  }

  function saveLayout(rows, columns) {
    const target = rows * columns;
    const finalButtonSelection =
      draftButtonSelection.length >= target
        ? draftButtonSelection.slice(0, target)
        : [
            ...draftButtonSelection,
            ...Array(target - draftButtonSelection.length).fill({ supplier_id: '', field: '', material: '' }),
          ];

    // Deactivate any active document that falls outside the new grid
    const removedButtons = draftButtonSelection.slice(target);
    if (removedButtons.some((btn) => isButtonActive(btn))) {
      closeActiveDocument();
    }

    isSavingLayout.current = true;
    setNRows(rows);
    setNColumns(columns);
    updateButtonSelection(finalButtonSelection, rows, columns, linkedDevices);

    logEventCustom(EVENT_NAMES.LAYOUT_SAVE, {
      n_rows: rows,
      n_columns: columns,
      // Empty slots are saved as placeholders, so count the ones an operator can actually stamp.
      num_buttons_configured: finalButtonSelection.filter((button) => button.supplier_id || button.field).length,
    });
  }

  React.useEffect(() => {
    // Skip when saveLayout already wrote the correct data to avoid a double-write
    if (isSavingLayout.current) {
      isSavingLayout.current = false;
      return;
    }

    /*
        If the number of rows or columns changes externally (e.g. another device),
        reconcile buttonSelection with the new grid dimensions.
        */
    if (buttonSelection.every((selection) => selection.supplier_id === '' && selection.field === '')) {
      return;
    }

    const numButtons = nRows * nColumns;
    let newButtonSelection = buttonSelection;
    if (buttonSelection.length < numButtons) {
      newButtonSelection = [...buttonSelection];
      for (let i = buttonSelection.length; i < numButtons; i++) {
        newButtonSelection.push({ supplier_id: '', field: '', material: '' });
      }
    } else if (buttonSelection.length > numButtons) {
      newButtonSelection = buttonSelection.slice(0, numButtons);

      const removedButtons = buttonSelection.slice(numButtons);
      if (removedButtons.some((button) => isButtonActive(button))) {
        closeActiveDocument();
      }
    }

    updateButtonSelection(newButtonSelection, nRows, nColumns, linkedDevices);
  }, [nRows, nColumns]);

  // Add event listener to close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      const sideMenu = document.querySelector('.side-menu');
      const menuButton = document.querySelector('.menu-button');

      if (sideMenu && !sideMenu.contains(event.target) && !menuButton?.contains(event.target)) {
        setShowSideMenu(false);
      }
    };

    if (showSideMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSideMenu]);

  return (
    <RecipeDisplayProvider
      currentUser={currentUser}
      shiftInfo={shiftInfo}
      settings={settings}
      productionRuns={enrichedProductionRuns}
      productionEvents={productionEvents}
      ingredients={ingredients}
      linkedDevices={linkedDevices}
      fallbackUserLabel={t('common_user')}
    >
      <div className='flex flex-col h-screen w-screen overflow-hidden bg-white text-[#16192c] dark:bg-[#0f172a] dark:text-[#fafafa]'>
        <TopNavigation
          currentUser={currentUser}
          velocities={velocities}
          editMode={editMode}
          toggleEdit={toggleEdit}
          setShowSideMenu={setShowSideMenu}
          switchDevice={switchDevice}
          shiftInfo={shiftInfo}
          settings={settings}
          conveyorCode={conveyorCode}
          linkedDevices={linkedDevices}
          appSettings={appSettings}
          draftRows={draftRows}
          draftColumns={draftColumns}
          setDraftRows={setDraftRows}
          setDraftColumns={setDraftColumns}
          onSaveLayout={saveLayout}
          isRecipeControlEnabled={isRecipeControlEnabled}
        />

        <SideMenu
          showSideMenu={showSideMenu}
          setShowSideMenu={setShowSideMenu}
          uiMode={uiMode}
          updateUiMode={updateUiMode}
          onOpenUserModal={setActiveUserModal}
          setConfirmLogout={setConfirmLogout}
          currentUser={currentUser}
          shiftInfo={shiftInfo}
          settings={settings}
        />
        {isRecipeControlEnabled && (
          <div className='shrink-0'>
            <RecipeDisplay deviceName={deviceName} nColumns={displayNColumns} />
          </div>
        )}

        {isLoadingInitialData ? (
          <LoadingScreen loading='Loading...' fullscreen={false} />
        ) : (
          <ButtonGrid
            buttonSelection={displayButtonSelection}
            nRows={displayNRows}
            nColumns={displayNColumns}
            editMode={editMode}
            isButtonActive={isButtonActive}
            selectionEdited={editMode ? draftSelectionEdited : selectionEdited}
            toggleSelection={toggleSelection}
            suppliers={suppliers}
            stockFields={stockFields}
            activeDocument={activeDocument}
            productionRuns={enrichedProductionRuns}
            ingredients={ingredients}
            isRecipeControlEnabled={isRecipeControlEnabled}
          />
        )}

        {activeUserModal === USER_MODAL_TYPES.SWITCH && (
          <SwitchUserModal settings={settings} onClose={() => setActiveUserModal(null)} />
        )}

        {activeUserModal === USER_MODAL_TYPES.ADD && <AddUserModal onClose={() => setActiveUserModal(null)} />}

        {activeUserModal === USER_MODAL_TYPES.DELETE && (
          <DeleteUserModal settings={settings} onClose={() => setActiveUserModal(null)} />
        )}

        {confirmLogout && (
          <LogoutConfirmationModal
            onCancel={() => setConfirmLogout(false)}
            onConfirm={() => {
              setConfirmLogout(false);
              signOut(auth);
            }}
          />
        )}

        {showStampTimeoutModal && (
          <StampTimeoutModal
            handleYes={() => {
              logEventCustom(EVENT_NAMES.STAMP_EXTEND, {
                duration_seconds: getElapsedSeconds(activeDocument?.data?.start_timestamp),
              });
              extendStampTimeout();
            }}
            handleNo={() => {
              // Letting the warning close the stamp is a different signal from stopping it by hand.
              logEventCustom(EVENT_NAMES.STAMP_STOP, {
                ...stampEventParams(activeDocument?.data),
                duration_seconds: getElapsedSeconds(activeDocument?.data?.start_timestamp),
                reason: 'timeout',
              });
              closeActiveDocument();
            }}
            selection={activeDocument?.data}
            suppliers={suppliers}
          />
        )}

        {showShiftTimeoutModal && (
          <ShiftTimeoutModal
            handleClose={() => {
              setShowShiftTimeoutModal(false);
              setActiveUserModal(USER_MODAL_TYPES.SWITCH);
            }}
          />
        )}
      </div>
    </RecipeDisplayProvider>
  );
}
